import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';

export interface ItineraryHeaderProps {
  title: string;
  standardTitle?: string;
  alternativeTitle?: string;
}

type ItineraryType = 'standard' | 'alternative';
const QUERY_PARAM = 'alternative';
const ItineraryHeader: React.FC<ItineraryHeaderProps> = ({
  title,
  standardTitle,
  alternativeTitle,
}) => {
  const searchParams = new URLSearchParams(window.location.search);
  const isAlternative = searchParams.has(QUERY_PARAM);

  const [selectedItinerary, setSelectedItinerary] = useState<ItineraryType>(
    isAlternative ? 'alternative' : 'standard',
  );

  const handleItineraryChange = (itinerary: ItineraryType) => {
    setSelectedItinerary(itinerary);
    const currentQueryParams = new URLSearchParams(window.location.search);
    if (itinerary === 'standard') {
      currentQueryParams.delete(QUERY_PARAM);
    } else {
      currentQueryParams.set(QUERY_PARAM, 'true');
    }
    window.location.search = `?${currentQueryParams.toString()}`;
  };

  return (
    <HeaderBox>
      <TitleBox>
        <Typography variant="H3">{title}</Typography>
      </TitleBox>
      {standardTitle && alternativeTitle && (
        <ButtonBox>
          <StyledButton
            variant={selectedItinerary === 'standard' ? 'contained' : 'outlined'}
            onClick={() => handleItineraryChange('standard')}
          >
            {standardTitle}
          </StyledButton>
          <StyledButton
            variant={selectedItinerary === 'alternative' ? 'contained' : 'outlined'}
            onClick={() => handleItineraryChange('alternative')}
          >
            {alternativeTitle}
          </StyledButton>
        </ButtonBox>
      )}
    </HeaderBox>
  );
};

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginBottom: theme.spacing(2),
  '@media (max-width: 960px)': {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

const ButtonBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0),
  '@media (max-width: 960px)': {
    marginTop: theme.spacing(2),
    flex: 1, // fill horizontal on mobile
  },
}));

const TitleBox = styled(Box)(({ theme }) => ({
  flex: 1,
  '@media (max-width: 960px)': {
    marginBottom: theme.spacing(2),
  },
}));

const StyledButton = styled(Button)(({ theme, variant }) => ({
  margin: 0,
  borderRadius: 0,
  border: 'none',
  padding: theme.spacing(1, 2),
  backgroundColor:
    variant == 'outlined'
      ? theme.palette.principal.grey30
      : theme.palette.principal.black,
  color:
    variant == 'outlined'
      ? theme.palette.principal.black
      : theme.palette.principal.grey30,
  '&:hover': {
    backgroundColor:
      variant == 'outlined'
        ? theme.palette.principal.black
        : theme.palette.principal.grey30,
    color:
      variant == 'outlined'
        ? theme.palette.principal.grey30
        : theme.palette.principal.black,
    border: 'none',
  },
  '@media (max-width: 960px)': {
    width: '50%',
  },
}));

export default ItineraryHeader;
